import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Copy as CopyIcon, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

export default function VisibilityDialog({ listing, onClose, onSuccess }) {
    const [isOff, setIsOff] = useState(!!listing.is_off_market);
    const [coEmail, setCoEmail] = useState(listing.co_listing_agent_email || "");
    const [splitPct, setSplitPct] = useState(listing.commission_split_pct || 0);
    const [submitting, setSubmitting] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [savedToken, setSavedToken] = useState(listing.off_market_token || null);
    const [coListStatus, setCoListStatus] = useState(listing.co_list_status || (listing.co_listing_agent_email ? "draft" : null));

    const submit = async () => {
        setSubmitting(true);
        try {
            const res = await api.patch(`/listings/${listing.id}/visibility`, {
                is_off_market: isOff,
                co_listing_agent_email: coEmail || null,
                commission_split_pct: Number(splitPct) || 0,
            });
            setSavedToken(res.data.off_market_token || null);
            toast.success("Visibility updated.");
            // Don't auto-close so the user can copy the off-market link
            if (!isOff) {
                onSuccess();
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not update");
        } finally {
            setSubmitting(false);
        }
    };

    const copyLink = async () => {
        const url = `${window.location.origin}/listings/private/${savedToken}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Private link copied.");
        } catch {
            window.prompt("Private listing link:", url);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="visibility-dialog" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display text-foreground">Listing visibility & split</DialogTitle>
                    <DialogDescription className="font-app text-muted-foreground">
                        Toggle off-market for private deals. Configure agent-to-agent commission splits.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-5 pt-2">
                    <div className="flex items-start justify-between gap-3 p-3 rounded-md bg-secondary/40 dark:bg-card/60 border border-border">
                        <div className="flex-1">
                            <Label htmlFor="off-market-toggle" className="font-display text-sm font-bold text-foreground">Off-market</Label>
                            <p className="font-app text-xs text-muted-foreground mt-0.5">
                                Hides the listing from public search. Only people with the private link can view.
                            </p>
                        </div>
                        <Switch id="off-market-toggle" checked={isOff} onCheckedChange={setIsOff} data-testid="visibility-off-market-toggle" />
                    </div>

                    {isOff && savedToken && (
                        <div className="p-3 rounded-md bg-coral/5 dark:bg-coral/10 border border-coral/30" data-testid="visibility-private-link">
                            <Label className="text-[10px] uppercase tracking-wider font-app text-coral mb-2 block">Private link</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    readOnly
                                    value={`${window.location.origin}/listings/private/${savedToken}`}
                                    className="bg-background text-foreground text-xs font-mono"
                                />
                                <Button size="sm" variant="outline" onClick={copyLink} data-testid="visibility-copy-link-btn" className="font-app shrink-0">
                                    <CopyIcon className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                    )}

                    <div>
                        <Label className="text-xs font-app text-muted-foreground">Co-listing agent email</Label>
                        <Input
                            type="email"
                            value={coEmail}
                            onChange={(e) => setCoEmail(e.target.value)}
                            placeholder="agent@example.com"
                            data-testid="visibility-co-email"
                            className="bg-background text-foreground"
                        />
                        <p className="font-app text-[11px] text-muted-foreground mt-1">
                            They'll be tagged as co-lister. Commission split paid out at deal close.
                        </p>
                    </div>
                    <div>
                        <Label className="text-xs font-app text-muted-foreground">Their split %</Label>
                        <Input
                            type="number"
                            value={splitPct}
                            onChange={(e) => setSplitPct(e.target.value)}
                            min={0} max={100}
                            data-testid="visibility-split-pct"
                            className="bg-background text-foreground"
                        />
                    </div>

                    {/* Phase H — Co-listing handshake */}
                    {coEmail && Number(splitPct) > 0 && Number(splitPct) < 100 && (
                        <div className="p-3 rounded-md bg-coral/5 dark:bg-coral/10 border border-coral/30">
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <Label className="font-display text-sm font-bold text-foreground flex items-center gap-1.5">
                                    <Handshake className="h-3.5 w-3.5 text-coral" /> Co-listing handshake
                                </Label>
                                {coListStatus === "accepted" ? (
                                    <Badge className="bg-fiji-green text-white text-[10px]">Accepted</Badge>
                                ) : coListStatus === "pending" ? (
                                    <Badge variant="outline" className="text-[10px] border-coral/40 text-coral">Pending</Badge>
                                ) : (
                                    <Badge variant="outline" className="text-[10px]">Not invited</Badge>
                                )}
                            </div>
                            <p className="font-app text-[11px] text-muted-foreground mb-2 leading-snug">
                                The split is only honoured at financial close once your co-agent formally accepts the invite.
                            </p>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={inviting || coListStatus === "accepted"}
                                onClick={async () => {
                                    setInviting(true);
                                    try {
                                        await api.post(`/listings/${listing.id}/co-list/invite`);
                                        toast.success(`Invite emailed to ${coEmail}.`);
                                        setCoListStatus("pending");
                                    } catch (err) {
                                        toast.error(formatApiError(err.response?.data?.detail) || "Could not invite");
                                    } finally {
                                        setInviting(false);
                                    }
                                }}
                                data-testid="visibility-co-list-invite-btn"
                                className="w-full font-app"
                            >
                                {inviting && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
                                {coListStatus === "pending" ? "Resend invite" : "Send co-listing invite"}
                            </Button>
                        </div>
                    )}
                </div>
                <DialogFooter className="mt-4">
                    <Button type="button" variant="ghost" onClick={onClose} className="font-app">Close</Button>
                    <Button onClick={submit} disabled={submitting} data-testid="visibility-save-btn" className="bg-coral hover:bg-coral-deep text-white">
                        {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
